import React, { useContext, useState, useEffect, useCallback } from "react";
import { Modal, Button } from "antd";
import { AuthContext } from "../context/AuthProvider";
import { useMobileWidgetContext } from "../context/MobileWidgetContext";
import LeoChatWidget from "./leo/LeoChatWidget";
import { getDebugMode, getCustomName } from "../helpers/constants";

const LEO_CONSENT_STORAGE_KEY = "leo_consumer_health_data_consent";

/**
 * AIAssistantWidget - Landlord-facing Leo chat widget
 *
 * This component wraps LeoChatWidget with landlord-specific API calls and configuration.
 */
const AIAssistantWidget = ({
  isVisible = true,
  /** Bump to programmatically open Leo (e.g. ?leaseRenewal= from email). */
  openSignal,
}) => {
  const { user, userInfo } = useContext(AuthContext);
  const { isFeedbackWidgetActive, isNotificationsPanelActive, setActiveWidget, clearActiveWidget } =
    useMobileWidgetContext();

  // Store confirmationData from the last assistant message that required confirmation.
  // It is forwarded with the user's next message so the backend can dispatch
  // the action directly without text-heuristic matching.
  const [pendingConfirmationData, setPendingConfirmationData] = useState(null);

  // Check for debug mode - if debug flag is in URL, always show tooltip
  const isDebugMode = getDebugMode();

  // Track if tooltip should be shown based on 10-minute expiration timer
  const getTooltipShowStatus = useCallback(() => {
    // If debug mode is active, bypass localStorage check (but still allow normal show/hide behavior)
    if (isDebugMode) {
      return true;
    }

    try {
      const dismissedTimestamp = localStorage.getItem(
        "leo-ai-tooltip-dismissed"
      );
      if (!dismissedTimestamp) {
        return true;
      }

      const dismissedTime = parseInt(dismissedTimestamp, 10);
      const currentTime = Date.now();
      const tenMinutesInMs = 10 * 60 * 1000;

      return currentTime - dismissedTime >= tenMinutesInMs;
    } catch (error) {
      return true;
    }
  }, [isDebugMode]);

  const [showWelcomeTooltip, setShowWelcomeTooltip] = useState(
    getTooltipShowStatus()
  );

  // Check periodically if tooltip should be shown again
  useEffect(() => {
    const checkTooltipStatus = () => {
      const shouldShow = getTooltipShowStatus();
      if (shouldShow && !showWelcomeTooltip) {
        setShowWelcomeTooltip(true);
      }
    };

    checkTooltipStatus();
    const interval = setInterval(checkTooltipStatus, 30000);
    return () => clearInterval(interval);
  }, [showWelcomeTooltip, getTooltipShowStatus]);

  const markTooltipAsDismissed = useCallback(() => {
    try {
      localStorage.setItem("leo-ai-tooltip-dismissed", Date.now().toString());
    } catch (error) {
      console.warn("Failed to save tooltip dismissal timestamp:", error);
    }
  }, []);

  // Storage key helpers
  const getChatStorageKey = useCallback(() => {
    return `leo-ai-chat-${user?.uid || "anonymous"}`;
  }, [user?.uid]);

  const getChatTimestampKey = useCallback(() => {
    return `leo-ai-chat-timestamp-${user?.uid || "anonymous"}`;
  }, [user?.uid]);

  // Fetch initial message from landlord API
  const fetchInitialMessage = useCallback(async () => {
    if (!user?.uid) {
      return {
        id: Date.now(),
        type: "ai",
        content:
          "Hi! I'm Leo AI, your personal property management AI. Please log in to get personalized assistance with your properties.",
        timestamp: new Date(),
      };
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error("API URL not configured");
      }

      const response = await fetch(
        `${apiUrl}/widget/initial-message/${user.uid}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.text) {
          return {
            id: Date.now(),
            type: "ai",
            content: data.text,
            timestamp: new Date(),
          };
        }
      }

      // Return a default message if API fails
      return {
        id: Date.now(),
        type: "ai",
        content:
          "Hi! I'm Leo AI, your personal property management AI. How can I help you today?",
        timestamp: new Date(),
      };
    } catch (error) {
      console.error("Error fetching initial message:", error);
      // Return a default message on error
      return {
        id: Date.now(),
        type: "ai",
        content:
          "Hi! I'm Leo AI, your personal property management AI. How can I help you today?",
        timestamp: new Date(),
      };
    }
  }, [user?.uid]);

  // Handle sending message via landlord API
  const handleSendMessage = useCallback(
    async (messageText, currentMessages) => {
      if (!user?.uid) {
        throw new Error("Please log in to use the AI assistant");
      }

      // Prepare chat history
      const chatHistory = currentMessages.map((msg) => ({
        from: msg.type === "user" ? "user" : "assistant",
        text: msg.content,
        timestamp: msg.timestamp.toISOString(),
        images: msg.images || [],
      }));

      chatHistory.push({
        from: "user",
        text: messageText,
        timestamp: new Date().toISOString(),
        images: [],
      });

      // Call the sendChat API
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error("API URL not configured");
      }

      // Capture and clear any pending structured confirmation before the fetch
      const confirmationToSend = pendingConfirmationData;
      if (confirmationToSend) {
        setPendingConfirmationData(null);
      }

      try {
        const response = await fetch(`${apiUrl}/sendChat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            clientId: user.uid,
            message: messageText,
            chatHistory: chatHistory,
            chatId: undefined,
            ...(confirmationToSend ? { confirmationData: confirmationToSend } : {}),
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 400) {
            try {
              const errorData = await response.json();
              if (
                Array.isArray(errorData) &&
                errorData[0]?.errors?.issues?.[0]?.message ===
                  "Message cannot be empty"
              ) {
                throw new Error("Please enter a message before sending.");
              }
              const validationMessage =
                errorData[0]?.errors?.issues?.[0]?.message ||
                "Invalid input. Please check your message.";
              throw new Error(validationMessage);
            } catch (parseError) {
              console.error("Failed to parse validation error:", parseError);
            }
          }

          const errorText = await response.text();
          throw new Error(`API call failed: ${response.status} - ${errorText}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const responseText = await response.text();
          throw new Error("Server returned non-JSON response");
        }

        const data = await response.json();

        if (data.error && data.status !== "consent_required") {
          throw new Error(data.error);
        }

        if (data.status === "consent_required" || data.consent_required) {
          return { consentRequired: true };
        }

        // If this response requires a follow-up confirmation, store the
        // structured confirmationData so the next message includes it.
        if (data.message?.requiresConfirmation && data.message?.confirmationData) {
          setPendingConfirmationData(data.message.confirmationData);
        }

        return {
          text:
            data.message?.text ||
            data.response ||
            data.message ||
            "Sorry, I encountered an error. Please try again.",
          decisionExplanation: data.decisionExplanation ?? null,
          requiresConfirmation: data.message?.requiresConfirmation ?? false,
          confirmationData: data.message?.confirmationData ?? null,
        };
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          throw new Error("Request timed out. Please try again.");
        }
        throw fetchError;
      }
    },
    [user?.uid, pendingConfirmationData]
  );

  // Get custom name from URL parameter for demo personalization
  const customName = getCustomName();
  const displayName = customName || userInfo?.firstName;

  const tooltipText = displayName
    ? `Hey ${displayName}! 👋 Chat with Leo AI about your properties`
    : "Hi there! 👋 Let's chat about your properties";

  const [consentModalVisible, setConsentModalVisible] = useState(false);
  const onConsentRequired = useCallback(() => setConsentModalVisible(true), []);
  const handleConsentAccept = useCallback(() => {
    try {
      localStorage.setItem(LEO_CONSENT_STORAGE_KEY, "true");
    } catch (e) {
      console.warn("Failed to store consent", e);
    }
    setConsentModalVisible(false);
  }, []);
  const handleConsentCancel = useCallback(
    () => setConsentModalVisible(false),
    []
  );

  return (
    <>
      <LeoChatWidget
        isVisible={isVisible}
        openSignal={openSignal}
        fetchInitialMessage={fetchInitialMessage}
        handleSendMessage={handleSendMessage}
        getChatStorageKey={getChatStorageKey}
        getChatTimestampKey={getChatTimestampKey}
        tooltipText={tooltipText}
        iconPath="/icons/leo/leo.png"
        buttonIconPath="/icons/leo/leo-chat.png"
        title="Leo AI Assistant"
        subtitle=""
        placeholder="Ask Leo AI about your properties..."
        showWelcomeTooltip={showWelcomeTooltip}
        onTooltipDismiss={markTooltipAsDismissed}
        requireAuth={!user?.uid}
        isFeedbackWidgetActive={
          isFeedbackWidgetActive || isNotificationsPanelActive
        }
        setActiveWidget={setActiveWidget}
        clearActiveWidget={clearActiveWidget}
        onConsentRequired={onConsentRequired}
        showAiDisclaimerFooter={true}
      />
      <Modal
        title="Consumer Health Data: Consent Required"
        open={consentModalVisible}
        onCancel={handleConsentCancel}
        footer={[
          <Button key="cancel" onClick={handleConsentCancel}>
            Cancel
          </Button>,
          <Button key="accept" type="primary" onClick={handleConsentAccept}>
            Accept
          </Button>,
        ]}
      >
        <p style={{ margin: 0 }}>
          It looks like you may be sharing health-related information. To
          process this under Washington State law, we need your explicit consent
          to our Consumer Health Data Privacy Policy.
        </p>
      </Modal>
    </>
  );
};

export default AIAssistantWidget;
